import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { Division, User, Notification, AppState } from '../types';
import { createQueryClient } from '../lib/react-query';
import { DivisionProvider } from './division-context';

// App Context Types
interface AppContextType extends AppState {
  setCurrentUser: (user: User | null) => void;
  setCurrentDivision: (division: Division | null) => void;
  setAvailableDivisions: (divisions: Division[]) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  removeNotification: (notificationId: string) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setLoading: (loading: boolean) => void;
}

// Action Types
type AppAction =
  | { type: 'SET_CURRENT_USER'; payload: User | null }
  | { type: 'SET_CURRENT_DIVISION'; payload: Division | null }
  | { type: 'SET_AVAILABLE_DIVISIONS'; payload: Division[] }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'TOGGLE_THEME' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Initial State
const initialState: AppState = {
  currentUser: null,
  currentDivision: null,
  availableDivisions: [],
  notifications: [],
  theme: 'light',
  sidebarOpen: false,
  loading: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    
    case 'SET_CURRENT_DIVISION':
      return { ...state, currentDivision: action.payload };
    
    case 'SET_AVAILABLE_DIVISIONS':
      return { ...state, availableDivisions: action.payload };
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
      };
    
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, isRead: true }
            : notification
        ),
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(
          notification => notification.id !== action.payload
        ),
      };
    
    case 'TOGGLE_THEME':
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', newTheme);
      }
      return { ...state, theme: newTheme };
    
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    default:
      return state;
  }
}

// Context
const AppContext = createContext<AppContextType | undefined>(undefined);

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Query Client
let queryClient: QueryClient | undefined;

function getQueryClient() {
  if (!queryClient) {
    queryClient = createQueryClient();
  }
  return queryClient;
}

// Provider Component
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const contextValue: AppContextType = {
    ...state,
    setCurrentUser: (user: User | null) => {
      dispatch({ type: 'SET_CURRENT_USER', payload: user });
    },
    setCurrentDivision: (division: Division | null) => {
      dispatch({ type: 'SET_CURRENT_DIVISION', payload: division });
    },
    setAvailableDivisions: (divisions: Division[]) => {
      dispatch({ type: 'SET_AVAILABLE_DIVISIONS', payload: divisions });
    },
    addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => {
      const newNotification: Notification = {
        ...notification,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
    },
    markNotificationAsRead: (notificationId: string) => {
      dispatch({ type: 'MARK_NOTIFICATION_READ', payload: notificationId });
    },
    removeNotification: (notificationId: string) => {
      dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
    },
    toggleTheme: () => {
      dispatch({ type: 'TOGGLE_THEME' });
    },
    toggleSidebar: () => {
      dispatch({ type: 'TOGGLE_SIDEBAR' });
    },
    setLoading: (loading: boolean) => {
      dispatch({ type: 'SET_LOADING', payload: loading });
    },
  };

  // Runtime configuration from environment variables
  // These are injected by docker-entrypoint.sh at container startup
  const getEnvVar = (key: string, defaultValue: string) => {
    // Try window._env_ first (runtime injection), fallback to process.env (build-time)
    if (typeof window !== 'undefined' && (window as any)._env_ && (window as any)._env_[key]) {
      const runtimeValue = (window as any)._env_[key];
      // Ignore placeholder values (used in Docker, replaced at runtime)
      if (runtimeValue && !runtimeValue.startsWith('__') && !runtimeValue.endsWith('__')) {
        return runtimeValue;
      }
    }
    return process.env[key] || defaultValue;
  };

  const authority = getEnvVar('REACT_APP_OIDC_AUTHORITY', 'https://integrator-3718641.okta.com/oauth2/default');
  const clientId = getEnvVar('REACT_APP_OIDC_CLIENT_ID', '0oaw2uhxk2Ae0w1ov697');
  const redirectUri = getEnvVar('REACT_APP_OIDC_REDIRECT_URI', 'http://localhost:3000/callback');
  const postLogoutRedirectUri = getEnvVar('REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI', 'http://localhost:3000');
  const scope = getEnvVar('REACT_APP_OIDC_SCOPE', 'openid profile email groups offline_access');

  // Debug: Log the config values
  console.log('OIDC Config:', {
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    scope
  });

  const oidcConfig = {
      authority,
      client_id: clientId,
      redirect_uri: redirectUri,
      post_logout_redirect_uri: postLogoutRedirectUri,
      scope,
      response_type: 'code',
      automaticSilentRenew: true,
      loadUserInfo: true, // Okta provides rich user info
      
      // CRITICAL: Disable subject check to allow switching between different users
      // Without this, we get "ID token subject does not match" error when adding a new account
      // This is safe because we're managing user sessions ourselves in UserSessionStorage
      validateSubOnSilentRenew: false,
      
      // Custom callback to handle post-signin cleanup
      onSigninCallback: (user: any) => {
        console.log('[OIDC] onSigninCallback triggered for user:', user?.profile?.preferred_username);
        
        // Check if we were adding a new account
        const addingAccount = sessionStorage.getItem('approvn_adding_account') === 'true';
        if (addingAccount) {
          console.log('[OIDC] New account sign-in detected, allowing subject change');
        }
        
        // Clean up URL parameters after callback
        window.history.replaceState({}, document.title, window.location.pathname);
      },
      
      // Let Okta auto-discovery handle metadata endpoints
      // Explicit metadata can cause URL construction issues
      
      userStore: typeof window !== 'undefined' 
          ? new WebStorageStateStore({ store: window.localStorage }) 
          : undefined,
  };

  // const oidcConfig = {
  //   authority: 'http://localhost:8080/realms/approvn',
  //   client_id: 'approvn-frontend',
  //   redirect_uri: 'http://localhost:3000',
  //   post_logout_redirect_uri: 'http://localhost:3000',
  //   scope: 'openid',
  //   response_type: 'code',
  //   automaticSilentRenew: true,
  //   loadUserInfo: false, // Disable since we'll get info from JWT token directly
  //   // Manual endpoint configuration since discovery might not be working
  //   metadata: {
  //     authorization_endpoint: 'http://localhost:8080/realms/approvn/protocol/openid-connect/auth',
  //     token_endpoint: 'http://localhost:8080/realms/approvn/protocol/openid-connect/token',
  //     userinfo_endpoint: 'http://localhost:8080/realms/approvn/protocol/openid-connect/userinfo',
  //     end_session_endpoint: 'http://localhost:8080/realms/approvn/protocol/openid-connect/logout',
  //     jwks_uri: 'http://localhost:8080/realms/approvn/protocol/openid-connect/certs',
  //     issuer: 'http://localhost:8080/realms/approvn',
  //   },
  //   userStore: typeof window !== 'undefined' ? new WebStorageStateStore({ store: window.localStorage }) : undefined,
  // };

  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={getQueryClient()}>
        <AppContext.Provider value={contextValue}>
          <DivisionProvider>
            {children}
            <ReactQueryDevtools initialIsOpen={false} />
          </DivisionProvider>
        </AppContext.Provider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
